import { formatTaxKey } from '../helpers';
import {
  NonTaxableTaxType,
  TaxKey,
  TaxOption,
  TaxOptions,
  TaxType,
} from '../types';
import { ipsiTaxOptions, ivaTaxOptions, igicTaxOptions } from './taxkey';

const sortTaxOptionsByTypeAndAmount = (options: TaxOptions): TaxOptions => {
  const groupedByType = Object.entries(options).reduce((acc, [key, value]) => {
    const { type } = value;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push([key, value]);
    return acc;
  }, {} as Record<TaxType, [string, TaxOption][]>);

  Object.keys(groupedByType).forEach((type) => {
    groupedByType[type as TaxType].sort(([, a], [, b]) => b.amount - a.amount);
  });

  const sortedEntries = Object.values(groupedByType).flat();
  return Object.fromEntries(sortedEntries);
};

export const taxOptions: TaxOptions = sortTaxOptionsByTypeAndAmount({
  ...ivaTaxOptions,
  ...igicTaxOptions,
  ...ipsiTaxOptions,
});

export const getTaxOption = (taxKey: TaxKey): TaxOption => {
  return taxOptions[taxKey];
};

export const defaultTaxOption = taxOptions.IVA_21;

export const salesEqualizationTax = {
  [formatTaxKey(TaxType.IVA, 2)]: 0.26,
  [formatTaxKey(TaxType.IVA, 4)]: 0.5,
  [formatTaxKey(TaxType.IVA, 5)]: 0.62,
  [formatTaxKey(TaxType.IVA, 7.5)]: 1,
  [formatTaxKey(TaxType.IVA, 10)]: 1.4,
  [formatTaxKey(TaxType.IVA, 21)]: 5.2,
};

export const isEuropeanNoSpainTaxKey = (taxKey: TaxKey) =>
  EuropeanNoSpainTaxKeys.includes(taxKey);

export const EuropeanNoSpainTaxKeys = [
  formatTaxKey(TaxType.IVA, 2.1), // France
  formatTaxKey(TaxType.IVA, 3), // Luxembourg
  formatTaxKey(TaxType.IVA, 4.8), // Ireland
  formatTaxKey(TaxType.IVA, 5.5), // France
  formatTaxKey(TaxType.IVA, 6), // Belgium, Greece, Portugal, Sweden
  formatTaxKey(TaxType.IVA, 7), // Germany, Malta
  formatTaxKey(TaxType.IVA, 8), // Luxembourg, Poland
  formatTaxKey(TaxType.IVA, 8.5), // France (Martinique, Guadeloupe, Réunion)
  formatTaxKey(TaxType.IVA, 9), // Bulgaria, Cyprus, Estonia, Ireland, Lithuania, Netherlands, Portugal (Azores), Romania
  formatTaxKey(TaxType.IVA, 9.5), // Slovenia
  formatTaxKey(TaxType.IVA, 12), // Belgium, Czech Republic, Latvia, Portugal (Madeira), Sweden
  formatTaxKey(TaxType.IVA, 13), // Austria, Croatia, France (Corsica), Greece, Portugal
  formatTaxKey(TaxType.IVA, 13.5), // Ireland
  formatTaxKey(TaxType.IVA, 14), // Finland, Luxembourg
  formatTaxKey(TaxType.IVA, 15), // Czech Republic
  formatTaxKey(TaxType.IVA, 16), // Portugal (Azores)
  formatTaxKey(TaxType.IVA, 17), // Greece (Aegean Islands), Luxembourg
  formatTaxKey(TaxType.IVA, 18), // Hungary, Malta
  formatTaxKey(TaxType.IVA, 19), // Austria (Jungholz, Mittelberg), Cyprus, Germany, Romania, Slovakia
  formatTaxKey(TaxType.IVA, 20), // Austria, Bulgaria, France, United Kingdom
  formatTaxKey(TaxType.IVA, 22), // Estonia, Italy, Portugal (Madeira), Slovenia
  formatTaxKey(TaxType.IVA, 23), // Ireland, Poland, Portugal, Slovakia
  formatTaxKey(TaxType.IVA, 24), // Greece
  formatTaxKey(TaxType.IVA, 25), // Croatia, Denmark, Sweden
  formatTaxKey(TaxType.IVA, 25.5), // Finland
  formatTaxKey(TaxType.IVA, 27), // Hungary
];

export const getVisibleTaxOptions = ({
  currentTax,
  onlyExpenses,
  isBai = false,
  showEuropeanTaxes = false,
  taxRegime = '',
  onlyIPSI = false,
  onlyIGIC = false,
  onlyIVA = false,
}: {
  currentTax: TaxKey;
  onlyExpenses?: boolean;
  isBai?: boolean;
  showEuropeanTaxes?: boolean;
  taxRegime?: string;
  onlyIPSI?: boolean;
  onlyIGIC?: boolean;
  onlyIVA?: boolean;
}): TaxOption[] => {
  const filteredOptions = Object.values(taxOptions).filter((tax) => {
    if (tax.onlyExpenses === true && !onlyExpenses) {
      return false;
    }
    const hideEuropeanTaxes =
      !showEuropeanTaxes && isEuropeanNoSpainTaxKey(tax.value);
    if (hideEuropeanTaxes) {
      const isCurrentTax = tax.value === currentTax;
      if (!isCurrentTax) {
        return false;
      }
    }
    return !tax.hide || tax.value === currentTax;
  });

  if (isBai) {
    return filteredOptions
      .filter((tax) => [TaxType.IVA].includes(tax.type))
      .filter((tax) => {
        if (taxRegime === '17') {
          if (
            tax.value.includes(NonTaxableTaxType.Exempt) ||
            tax.value.includes(NonTaxableTaxType.NotSubject) ||
            tax.value.includes(NonTaxableTaxType.ReverseCharge)
          ) {
            return false;
          }
          return true;
        }
        if (taxRegime === '53') {
          if (tax.value.includes(NonTaxableTaxType.NotSubject)) {
            return true;
          }
          return false;
        }

        if (taxRegime === '10') {
          if (
            tax.value.includes(NonTaxableTaxType.Exempt) ||
            tax.value.includes(NonTaxableTaxType.ReverseCharge)
          ) {
            return false;
          }

          return true;
        }

        if (taxRegime === '02') {
          if (tax.value.includes(NonTaxableTaxType.Exempt)) {
            return true;
          }
          return false;
        }
        return true;
      });
  }
  return filteredOptions
    .map((tax) => {
      const ivaZeros = [
        formatTaxKey(TaxType.IVA, 0),
        formatTaxKey(TaxType.IGIC, 0),
        formatTaxKey(TaxType.IPSI, 0),
      ];

      const shouldAddTypeInLabel =
        tax.amount === 0 && !ivaZeros.includes(tax.value);
      return {
        ...tax,
        label: shouldAddTypeInLabel ? `${tax.label} ${tax.type}` : tax.label,
      };
    })
    .filter((tax) => {
      if (tax.value === currentTax) {
        return true;
      }
      if (onlyIPSI && tax.type !== TaxType.IPSI) {
        return false;
      }
      if (onlyIGIC && tax.type !== TaxType.IGIC) {
        return false;
      }
      if (onlyIVA && tax.type !== TaxType.IVA) {
        return false;
      }
      return true;
    });
};
